'use client';

import React from 'react';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import {
  IBackgroundImages,
  IGuideHomeTopRated,
} from '@/api/home/types';
import { BestResorts } from '@/components/home/BestResorts';
import { IRegionAllPathsInfo, IRegionFeaturedResort } from '@/api/region/types';
import { Routes } from '@/routes';
import IntegrityError from '@/page-components/400';
import RateResortSearch from '@/components/common/RateResortSearch/RateResortSearch';
import { ELocale } from '@/i18n/localeEnum';
import { getLocale } from '@/i18n/locale';
import WeSkiWidget from '@/components/common/PlanYourTrip/WeSki/WeSkiWidget';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { BrowseByGroup, BrowseSection } from '@/components/global/BrowseByGroup';

const SHOULD_SHOW_WESKI = ![ELocale.IT_IT].includes(getLocale() as ELocale);

export type GuidePageProps = {
  guideHomeTopRatedFirstPage: IGuideHomeTopRated;
  regions: IRegionAllPathsInfo;
  featured: IRegionFeaturedResort;
  backgroundImage: IBackgroundImages;
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '3.0';
export const TEMPLATE_NAME = 'Guide Home Page';

export default function GuidePage({
  guideHomeTopRatedFirstPage, regions, featured, backgroundImage,
}: GuidePageProps) {
  const { t } = useOTSTranslation('common');

  if (!featured || !backgroundImage) {
    return <IntegrityError />;
  }

  const regionList = regions?.regions ?? [];
  const sections: Array<BrowseSection> = (regions?.groups ?? []).map((group) => ({
    group,
    regions: regionList.filter((r) => r.group === group.uuid),
  }));

  return (
    <AdUnitContext
      adType="home"
      adTKey="other travel"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeaderNav current={HeaderNavCurLink.TRIP_PLANNING} />
      <TopAd id="ski-resorts" />
      <Container width="xl-only" className="content-container">
        <div className="mt-4 space-y-10 px-4 pb-16 md:mt-8 md:px-0">
          <header className="space-y-4">
            <Heading
              level={1}
            >
              {t('guide.home.heroTitle')}
            </Heading>
            <Text variant="default" className="text-base leading-relaxed text-foreground/85">
              {t('guide.home.seoText')}
            </Text>
          </header>

          {guideHomeTopRatedFirstPage?.data && (
            <BestResorts
              pageType="global"
              resortTopRatingOverall={guideHomeTopRatedFirstPage.data}
              displayMode="grid"
            />
          )}

          {SHOULD_SHOW_WESKI && <WeSkiWidget destinationId="flexible" />}

          <BrowseByGroup sections={sections} linkFor={Routes.regionTripPlanningOverview} />

          <RateResortSearch regionTitle={featured.details.region.title} />

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
          />
        </div>
      </Container>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
